import React, { useMemo, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Heading } from '../../components/heading';
import { Text } from '../../components/text';
import { useClients, useData } from '../../contexts/DataContext';
import { usePortalView } from '../../contexts/PortalViewContext';
import { getClientExpiryStatus } from '../../utils/documentExpiry';
import {
  ExclamationCircleIcon,
  FolderOpenIcon,
} from '@heroicons/react/24/outline';

const ClientPortalExpedientes = () => {
  const navigate = useNavigate();
  const { clientsList: allClients, clientsLoading } = useClients();
  const { activeClienteId } = usePortalView();
  // Scoped to the active fiado context. With one empresa in scope the list
  // page is skipped entirely (redirect below).
  const clientsList = useMemo(
    () => (activeClienteId ? allClients.filter((c) => c.id === activeClienteId) : allClients),
    [allClients, activeClienteId]
  );

  // The Empresa page owns the expediente now — an active fiado context (or a
  // single-company account) lands straight on its Expediente tab.
  useEffect(() => {
    if (activeClienteId) {
      navigate(`/app/portal/cliente/${activeClienteId}?tab=expediente`, { replace: true });
      return;
    }
    if (!clientsLoading && clientsList.length === 1) {
      navigate(`/app/portal/cliente/${clientsList[0].id}?tab=expediente`, { replace: true });
    }
  }, [activeClienteId, clientsLoading, clientsList, navigate]);
  const { linksById } = useData();

  const clientStatuses = useMemo(() => {
    const map = {};
    clientsList.forEach((c) => {
      map[c.id] = getClientExpiryStatus(c.links || [], linksById);
    });
    return map;
  }, [clientsList, linksById]);

  if (clientsLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-azul-surelia" />
      </div>
    );
  }

  if (!clientsList || clientsList.length === 0) {
    return (
      <div className="container mx-auto px-4 py-6">
        <Heading level={1} className="text-azul-surelia mb-6">Expedientes</Heading>
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-12 text-center">
          <FolderOpenIcon className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <Heading level={3} className="mb-2">No tienes empresas asignadas</Heading>
          <Text className="text-gray-500">
            Contacta a tu agente de fianzas para que te asigne acceso a tus empresas.
          </Text>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-6">
      <div className="mb-6">
        <Heading level={1} className="text-azul-surelia mb-2">Expedientes</Heading>
        <Text className="text-zinc-600">
          Selecciona una empresa para ver el estado de sus documentos.
        </Text>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {clientsList.map((client) => {
          const clientLinks = (client.links || [])
            .map((id) => linksById[id])
            .filter(Boolean);
          const expiryInfo = clientStatuses[client.id] || { expiredCount: 0, warningCount: 0, worst: 'valid' };

          const statusDotColor =
            expiryInfo.worst === 'expired'
              ? 'bg-red-500'
              : expiryInfo.worst === 'warning'
              ? 'bg-amber-400'
              : 'bg-green-500';

          return (
            <button
              key={client.id}
              onClick={() => navigate(`/app/portal/cliente/${client.id}?tab=expediente`)}
              className="relative bg-white rounded-xl border border-gray-200 shadow-sm p-6 text-left hover:border-azul-surelia/30 hover:shadow-md transition-all"
            >
              <div
                className={`absolute top-4 right-4 w-3 h-3 rounded-full ${statusDotColor} ring-2 ring-offset-1 ${
                  expiryInfo.worst === 'expired'
                    ? 'ring-red-500/30'
                    : expiryInfo.worst === 'warning'
                    ? 'ring-amber-400/30'
                    : 'ring-green-500/30'
                }`}
              />

              <Heading level={3} className="mb-1 truncate pr-6">
                {client.nombreEmpresa || 'Sin nombre'}
              </Heading>
              {client.rfc && (
                <Text className="text-xs text-gray-400 mb-4 font-mono">{client.rfc}</Text>
              )}

              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <Text className="text-sm text-gray-600">Expedientes</Text>
                  <Text className="text-sm font-semibold">{clientLinks.length}</Text>
                </div>

                {expiryInfo.expiredCount > 0 && (
                  <div className="flex items-center gap-2 text-red-600 bg-red-50 rounded-lg px-3 py-2">
                    <ExclamationCircleIcon className="w-4 h-4 flex-shrink-0" />
                    <Text className="text-sm font-medium">
                      {expiryInfo.expiredCount} doc{expiryInfo.expiredCount > 1 ? 's' : ''} vencido{expiryInfo.expiredCount > 1 ? 's' : ''}
                    </Text>
                  </div>
                )}
                {expiryInfo.warningCount > 0 && (
                  <div className="flex items-center gap-2 text-amber-600 bg-amber-50 rounded-lg px-3 py-2">
                    <ExclamationCircleIcon className="w-4 h-4 flex-shrink-0" />
                    <Text className="text-sm font-medium">
                      {expiryInfo.warningCount} doc{expiryInfo.warningCount > 1 ? 's' : ''} por vencer
                    </Text>
                  </div>
                )}
                {expiryInfo.expiredCount === 0 && expiryInfo.warningCount === 0 && (
                  <div className="text-green-600 bg-green-50 rounded-lg px-3 py-2">
                    <Text className="text-sm font-medium">Documentos al día</Text>
                  </div>
                )}
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
};

export default ClientPortalExpedientes;
